import { isArray, isEmpty, isNil } from 'lodash/fp';
import { isNumeric, isDate, hasRhs, OPS, isBoolean } from 'app/lib/utils/sql';
import {
    and,
    condition,
    from,
    group,
    having,
    identifier,
    limit,
    or,
    order,
    parens,
    select,
    sort,
    sql,
    value,
    where,
    isNullCondition,
    betweenOp,
    inOperator,
    formula
} from 'app/lib/utils/sql_generator.js';

export function dimension({ column, bin }) {
    if (bin) {
        const alias = identifier(dimensionAlias({ column, bin }));

        if (isDate(column.type)) {
            return `DATE_TRUNC(${bin}, ${identifier(column.name)}) AS ${alias}`;
        }

        return `FLOOR(${identifier(column.name)} / ${bin}) * ${bin} AS ${alias}`;
    }

    return identifier(column.name);
}

export function dimensionAlias({ column, bin }) {
    if (bin) {
        return `${column.name}_${bin}`;
    }

    return column.name;
}

export function measure({ column, fn }) {
    const alias = identifier(measureAlias({ column, fn }));

    switch (fn) {
        case 'COUNT DISTINCT':
            return `COUNT(DISTINCT ${identifier(column.name)}) AS ${alias}`;
        case 'MIN LENGTH':
            return `MIN(LENGTH(${identifier(column.name)})) AS ${alias}`;
        case 'MAX LENGTH':
            return `MAX(LENGTH(${identifier(column.name)})) AS ${alias}`;
        case 'AVG LENGTH':
            return `AVG(LENGTH(${identifier(column.name)})) AS ${alias}`;
        default:
            return `${fn}(${column.isRowCount ? '*' : identifier(column.name)}) AS ${alias}`;
    }
}

export function measureAlias({ column, fn }) {
    if (column.isRowCount) {
        return 'ROW_COUNT';
    }

    const suffix = fn.replace(' ', '_');

    return `${column.name}_${suffix}`;
}

function columns({ dimensions, measures }) {
    if (isEmpty(dimensions) && isEmpty(measures)) {
        return '*';
    }

    return [...(dimensions?.map(dimension) ?? []), ...(measures?.map(measure) ?? [])];
}

function column(columnName, trimWhitespace) {
    const name = columnName ? identifier(columnName) : '<column>';

    if (trimWhitespace) {
        return `TRIM(${name})`;
    }

    return name;
}

function empty({ column: filterColumn, trimWhitespace }, negate) {
    const lhs = column(filterColumn?.name, trimWhitespace);

    return parens(
        (negate ? and : or)(
            condition(lhs, negate ? '!=' : '=', "''"),
            condition(lhs, negate ? 'IS NOT NULL' : 'IS NULL')
        )
    );
}

function between({ column, value: rhs }, negate) {
    const lhs = column?.name ? identifier(column?.name) : '<column>';

    return parens(
        (negate ? or : and)(
            condition(lhs, negate ? '<' : '>=', value(rhs?.[0])),
            condition(lhs, negate ? '>' : '<=', value(rhs?.[1]))
        )
    );
}

function relative({ value, column }) {
    const unit = value ? value.unit : '<unit>';
    const amount = value ? value.amount * (value.beforeAfter === 'BEFORE' ? -1 : 1) : '<n>';
    const origin = column.type === 'DATE' ? 'CURRENT_DATE()' : 'CURRENT_TIMESTAMP()';

    return `DATEADD(${unit}, ${amount}, ${origin})`;
}

export const date = (value) => value.replace('T', ' ');

function rhs(filter) {
    if (filter.isRelative) {
        return relative(filter);
    }

    if (isBoolean(filter.column?.type)) {
        return filter.value;
    }

    if (filter.value && !isArray(filter.value) && !filter.value.isLiteral) {
        return identifier(filter.value?.name);
    }

    if (isNumeric(filter.column?.type)) {
        if (isArray(filter.value)) {
            return value(filter.value.map(Number));
        }

        if (isNil(filter.value?.name)) {
            return '<value>';
        }

        return value(Number(filter.value.name));
    }

    if (isDate(filter.column?.type)) {
        if (isNil(filter.value?.name)) {
            return "'<value>'";
        }

        return value(date(filter.value.name));
    }

    if (isArray(filter.value)) {
        return value(filter.value);
    }

    const s = filter.value?.name ?? '<value>';

    switch (filter.operator?.name) {
        case OPS.STARTS_WITH:
        case OPS.NOT_STARTS_WITH:
            return `'${s}%'`;
        case OPS.CONTAINS:
        case OPS.NOT_CONTAINS:
            return `'%${s}%'`;
        case OPS.ENDS_WITH:
        case OPS.NOT_ENDS_WITH:
            return `'%${s}'`;
        case OPS.IN:
        case OPS.NOT_IN:
            return `('${s}')`;
        default:
            return `'${s}'`;
    }
}

function operator(name, type, caseSensitive) {
    switch (name) {
        case OPS.STARTS_WITH:
        case OPS.CONTAINS:
        case OPS.ENDS_WITH:
            return caseSensitive ? 'LIKE' : 'ILIKE';
        case OPS.NOT_STARTS_WITH:
        case OPS.NOT_CONTAINS:
        case OPS.NOT_ENDS_WITH:
            return caseSensitive ? 'NOT LIKE' : 'NOT ILIKE';
        case OPS.EQUALS:
            return caseSensitive || isDate(type) ? '=' : 'ILIKE';
        case OPS.NOT_EQUALS:
            return caseSensitive || isDate(type) ? '!=' : 'NOT ILIKE';
        case OPS.BEFORE:
            return '<';
        case OPS.NOT_BEFORE:
            return '>=';
        case OPS.AFTER:
            return '>';
        case OPS.NOT_AFTER:
            return '<=';
        case OPS.IS_NULL:
            return 'IS NULL';
        case OPS.IS_NOT_NULL:
            return 'IS NOT NULL';
        case OPS.IN:
            return 'IN';
        case OPS.NOT_IN:
            return 'NOT IN';
        default:
            return name ?? '=';
    }
}

export function filterCondition(filter) {
    if (isNil(filter)) {
        return "<column> = '<value>'";
    }

    switch (filter.operator?.name) {
        case 'is empty':
            return empty(filter, false);
        case 'is not empty':
            return empty(filter, true);
        case 'between':
            return between(filter, false);
        case 'not between':
            return between(filter, true);
        case 'arrays overlap':
            return formula(
                'ARRAYS_OVERLAP',
                `[${filter.value.map((val) => value(val))}]`,
                filter.column.name
            );
        default:
            return condition(
                column(filter.column?.name, filter.trimWhitespace),
                operator(filter.operator?.name, filter.column?.type, filter.caseSensitive),
                hasRhs(filter.operator?.name) ? rhs(filter) : ''
            );
    }
}

function dimensionFilters(query) {
    if (isEmpty(query.dimensionFilters)) {
        return null;
    }

    return Object.keys(query.dimensionFilters).map((name) => {
        const values = query.dimensionFilters[name];
        if (isArray(values)) {
            return values.includes(null)
                ? parens(
                      or(
                          isNullCondition(identifier(name)),
                          inOperator(
                              identifier(name),
                              values.filter((val) => val !== null)
                          )
                      )
                  )
                : inOperator(identifier(name), values);
        }
        const hasRangeFrom = !isNil(values.rangeFrom) && values.rangeFrom !== '';
        const hasRangeTo = !isNil(values.rangeTo) && values.rangeTo !== '';
        let rangeFrom = null;
        let rangeTo = null;

        if (hasRangeFrom) {
            rangeFrom = isDate(values.type) ? value(date(values.rangeFrom)) : values.rangeFrom;
        }
        if (hasRangeTo) {
            rangeTo = isDate(values.type) ? value(date(values.rangeTo)) : values.rangeTo;
        }

        if (hasRangeFrom && hasRangeTo) {
            return parens(betweenOp(identifier(name), rangeFrom, rangeTo));
        }
        if (hasRangeFrom) {
            return condition(identifier(name), '>=', rangeFrom);
        }
        if (hasRangeTo) {
            return condition(identifier(name), '<=', rangeTo);
        }
        return null;
    });
}

export function filters({ filters, andOrOr }) {
    return (andOrOr === 'OR' ? or : and)(filters?.map(filterCondition));
}

function summaryFilters(filters) {
    return and(filters?.map(filterCondition));
}

function groupKeys({ dimensions }) {
    if (isEmpty(dimensions)) {
        return [];
    }

    return Array(dimensions.length)
        .fill(null)
        .map((_, i) => i + 1);
}

export const selectClause = (query) => select(columns(query));
export const whereClause = (query) =>
    where(and(filters(query), parens(and(dimensionFilters(query)))));
export const groupByClause = (query) => group(groupKeys(query));
export const havingClause = (query) => having(summaryFilters(query.summaryFilters));
export const orderByClause = (query) =>
    order(query.sorts?.map(({ column, direction }) => sort(identifier(column.name), direction)));
export const limitClause = (query) => limit(query.limit);

export function buildSql(table, query) {
    return sql(
        selectClause(query),
        from(table),
        whereClause(query),
        groupByClause(query),
        havingClause(query),
        orderByClause(query),
        limitClause(query)
    );
}
